import type { Feature, Polygon, Position } from "geojson"

export const DRAWN_AREA_PREVIEW_PATH = "/scenes/synthetic-coastal-raster.jpg"
export const DRAWN_AREA_PREVIEW_ALT =
  "Synthetic natural-color satellite preview of a tropical coastal landscape"
export const DRAWN_AREA_PREVIEW_DISCLAIMER =
  "Generated demonstration preview. It is not satellite observation data."

export type DrawnAreaCoordinate = [number, number]

export type DrawnAreaProperties = {
  name: string
  source: "interactive-map-drawing"
  synthetic: true
  createdAt: string
  areaSquareKilometers: number
}

export type DrawnAreaFeature = Feature<Polygon, DrawnAreaProperties>

const EARTH_RADIUS_KM = 6_371.0088

function roundCoordinate(value: number) {
  return Math.round(value * 1_000_000) / 1_000_000
}

function coordinatesMatch(
  first: DrawnAreaCoordinate,
  second: DrawnAreaCoordinate
) {
  return first[0] === second[0] && first[1] === second[1]
}

function signedPlanarArea(coordinates: Array<DrawnAreaCoordinate>) {
  return coordinates.reduce((total, coordinate, index) => {
    const next = coordinates[(index + 1) % coordinates.length]
    return total + coordinate[0] * next[1] - next[0] * coordinate[1]
  }, 0)
}

export function calculatePolygonAreaSquareKilometers(
  coordinates: Array<DrawnAreaCoordinate>
) {
  if (coordinates.length < 3) return 0

  const meanLatitudeRadians =
    (coordinates.reduce((sum, coordinate) => sum + coordinate[1], 0) /
      coordinates.length) *
    (Math.PI / 180)
  const projected = coordinates.map(([longitude, latitude]) => {
    const longitudeRadians = longitude * (Math.PI / 180)
    const latitudeRadians = latitude * (Math.PI / 180)
    return [
      EARTH_RADIUS_KM * longitudeRadians * Math.cos(meanLatitudeRadians),
      EARTH_RADIUS_KM * latitudeRadians,
    ] as const
  })

  const doubledArea = projected.reduce((total, coordinate, index) => {
    const next = projected[(index + 1) % projected.length]
    return total + coordinate[0] * next[1] - next[0] * coordinate[1]
  }, 0)

  return Math.abs(doubledArea) / 2
}

export function createDrawnAreaFeature(
  rawCoordinates: Array<DrawnAreaCoordinate>,
  {
    createdAt,
  }: {
    createdAt: string
  }
): DrawnAreaFeature {
  if (Number.isNaN(Date.parse(createdAt))) {
    throw new Error("The drawn area needs a valid creation date.")
  }

  const coordinates = rawCoordinates
    .map(
      ([longitude, latitude]) =>
        [
          roundCoordinate(longitude),
          roundCoordinate(latitude),
        ] as DrawnAreaCoordinate
    )
    .filter(
      (coordinate, index, collection) =>
        index === 0 || !coordinatesMatch(coordinate, collection[index - 1])
    )

  if (
    coordinates.length > 1 &&
    coordinatesMatch(coordinates[0], coordinates.at(-1)!)
  ) {
    coordinates.pop()
  }

  if (
    coordinates.length < 3 ||
    coordinates.some(
      ([longitude, latitude]) =>
        !Number.isFinite(longitude) ||
        !Number.isFinite(latitude) ||
        longitude < -180 ||
        longitude > 180 ||
        latitude < -90 ||
        latitude > 90
    )
  ) {
    throw new Error("A polygon needs at least three valid map coordinates.")
  }

  const areaSquareKilometers = calculatePolygonAreaSquareKilometers(coordinates)

  if (areaSquareKilometers < 0.01) {
    throw new Error("Draw a larger polygon before finishing the area.")
  }

  const normalizedCoordinates =
    signedPlanarArea(coordinates) < 0 ? [...coordinates].reverse() : coordinates
  const closedRing: Array<Position> = [
    ...normalizedCoordinates,
    normalizedCoordinates[0],
  ]
  const longitudes = normalizedCoordinates.map((coordinate) => coordinate[0])
  const latitudes = normalizedCoordinates.map((coordinate) => coordinate[1])

  return {
    type: "Feature",
    id: `DRAWN-AOI-${createdAt.replace(/\D/g, "").slice(0, 14)}`,
    bbox: [
      Math.min(...longitudes),
      Math.min(...latitudes),
      Math.max(...longitudes),
      Math.max(...latitudes),
    ],
    properties: {
      name: "Custom drawn area",
      source: "interactive-map-drawing",
      synthetic: true,
      createdAt,
      areaSquareKilometers: Math.round(areaSquareKilometers * 100) / 100,
    },
    geometry: {
      type: "Polygon",
      coordinates: [closedRing],
    },
  }
}

export function formatDrawnAreaLabel(feature: DrawnAreaFeature) {
  return `Custom polygon · ${feature.properties.areaSquareKilometers.toLocaleString(
    "en-US",
    { maximumFractionDigits: 2 }
  )} km²`
}

export function serializeDrawnArea(feature: DrawnAreaFeature) {
  return `${JSON.stringify(feature, null, 2)}\n`
}

export function serializeDrawnAreaKml(feature: DrawnAreaFeature) {
  const coordinates = feature.geometry.coordinates[0]
    .map(([longitude, latitude]) => `${longitude},${latitude},0`)
    .join(" ")

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Custom area of interest</name>
    <description>Demonstration AOI boundary exported from Service Portal.</description>
    <Placemark>
      <name>${feature.properties.name}</name>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${coordinates}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>
  </Document>
</kml>
`
}
